import {useEffect, useMemo, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Check, ChevronsUpDown} from 'lucide-react';
import {Button} from '@/components/ui/button';
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover';
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from '@/components/ui/command';
import {citiesApi} from '@/features/cities/api';
import {cn} from '@/lib/utils';

type CityLike = {
  id: number | string;
  name?: string;
  label?: string;
  department?: string;
};

type CitySearchSelectProps = {
  value?: number | string | null;
  onChange: (value: number) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  minSearchLength?: number;
  disabled?: boolean;
  displayValue?: string;
  emptyMessage?: string;
};

function cityLabel(city?: CityLike | null) {
  if (!city) return '';
  return city.label || [city.name, city.department].filter(Boolean).join(' - ') || city.name || String(city.id);
}

export function CitySearchSelect({
  value,
  onChange,
  placeholder = 'Seleccionar ciudad',
  searchPlaceholder = 'Buscar ciudad...',
  minSearchLength = 3,
  disabled = false,
  displayValue,
  emptyMessage = 'No se encontraron resultados.',
}: CitySearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selectedLabel, setSelectedLabel] = useState<string | undefined>(displayValue);

  useEffect(() => {
    setSelectedLabel(displayValue);
  }, [displayValue]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [query]);

  const shouldSearch = debounced.length >= minSearchLength;

  const {data, isFetching} = useQuery({
    queryKey: ['city-search-select', debounced],
    queryFn: () => citiesApi.search({q: debounced, limit: 50}),
    enabled: shouldSearch && open,
  });

  const cities: CityLike[] = Array.isArray(data) ? data : (data as any)?.data || [];

  const resolvedLabel = useMemo(() => {
    if (selectedLabel) return selectedLabel;
    if (displayValue) return displayValue;
    if (value == null || value === '') return '';
    const found = cities.find((item) => String(item.id) === String(value));
    return cityLabel(found);
  }, [cities, displayValue, selectedLabel, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">{resolvedLabel || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} />
          <CommandList>
            {!shouldSearch && <CommandEmpty>Escribe al menos {minSearchLength} caracteres.</CommandEmpty>}
            {shouldSearch && isFetching && <CommandEmpty>Buscando...</CommandEmpty>}
            {shouldSearch && !isFetching && cities.length === 0 && <CommandEmpty>{emptyMessage}</CommandEmpty>}
            {shouldSearch && !isFetching && cities.length > 0 && (
              <CommandGroup>
                {cities.map((city) => {
                  const label = cityLabel(city);
                  return (
                    <CommandItem
                      key={city.id}
                      value={label}
                      onSelect={() => {
                        onChange(Number(city.id));
                        setSelectedLabel(label);
                        setOpen(false);
                      }}
                    >
                      <Check className={cn('mr-2 h-4 w-4', String(value) === String(city.id) ? 'opacity-100' : 'opacity-0')} />
                      {city.name || city.label || city.id}
                      {city.department ? <span className="ml-2 text-muted-foreground">- {city.department}</span> : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
